import { FC, useContext, useMemo, useRef } from "react";
import { ColumnConfig, SortDirections, WafflesPaginationProps } from "@app/shared/waffles";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import { sortProviderNetworkTypes } from "@app/shared/enums/providerNetworkTypeEnum";
import ProviderActionStatusBadge from "@app/shared/ProviderActionStatusBadge";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { WafflesThemes } from "@app/shared/waffles/exports";
import { ProviderBadgeStyle } from "@app/projects/enumerators";
import WaffleRowTooltip from "@app/shared/waffles/WaffleRowTooltip";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { Waffles } from "@app/shared/waffles/Waffles";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { GapImpactPaginationResult, ProviderHubContractEntityProviders, ProviderHubContractEntityProvidersTableData } from "@app/projects/network-assessment/provider-hub/interfaces";
import { ProviderProfileReferrerEnum } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileReferrerEnum";
import ProviderHubService from "@app/projects/network-assessment/provider-hub/providerHubService";
import { ProviderScoreColumnHeader } from "@app/projects/network-assessment/provider-hub/ProviderScoreColumnHeader";
import { NO_ACTIONS_APPLIED_LABEL, formatCountiesImpacted, formatGapImpactCount, formatScore, formatSpecialtyWithCode, sortPredictedSpecialtyNames } from "@app/projects/network-assessment/provider-hub/utility";
import { NetworkIntegrityNotificationFlag } from "@app/projects/network-assessment/provider-hub/network-integrity-components/NetworkIntegrityNotificationFlag";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";

interface ContractEntityProfileTableProps {
    contractEntityProviderTableData: GapImpactPaginationResult<ProviderHubContractEntityProviders>;
    loading?: boolean;
    pageIndex: number;
    pageSize: number;
    resetToFirstPage: boolean;
    setContractEntityProviderTableData: React.Dispatch<React.SetStateAction<GapImpactPaginationResult<ProviderHubContractEntityProviders>>>;
    setPageIndex: React.Dispatch<React.SetStateAction<number>>;
    setPageSize: React.Dispatch<React.SetStateAction<number>>;
    setResetToFirstPage: React.Dispatch<React.SetStateAction<boolean>>;
}
/* oxlint-disable sort-keys */
const ContractEntityProfileTable: FC<React.PropsWithChildren<ContractEntityProfileTableProps>> = (props: ContractEntityProfileTableProps) => {
    const projectContext = useContext(CountyProjectContext);
    const providerHubContractEntityContext = useContext(ProviderHubContext);
    const { selectedContractEntity } = providerHubContractEntityContext;
    const providerHubService = useRef(new ProviderHubService()).current;
    const featureFlagContext = useContext(FeatureFlagContext);

    const useRegionStatusBreakdown = useMemo<boolean>(() => {
        return props.contractEntityProviderTableData.useGapsByRegionStatus === true;
    }, [props.contractEntityProviderTableData]);

    const providerHubContractEntitiesTableColumnConfig: ColumnConfig = {
        nameElement: {
            displayText: "Name",
            sizeContentToFit: true,
            sticky: true,
            testId: "provider-hub_contract-entity-table-provider-name",
            noSort: true
        },
        identifier: {
            displayText: "Identifier",
            sizeContentToFit: true,
            sticky: true,
            testId: "provider-hub_contract-entity-table-identifier",
            noSort: true
        },
        providerNetworkTypes: {
            displayText: "Provider Source",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-provider-source",
            tooltip: "Indicates the original database from which the provider data was obtained",
            noSort: true
        },
        providerActionStatuses: {
            displayText: "Status",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-status",
            tooltip: "Indicates any actions that have been applied to either a specific access point or across access points for a contract entity",
            noSort: true
        },
        affiliationStrength: {
            displayText: "Affiliation Strength",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-affiliationstrength",
            tooltip: "Indicates the strength of the affiliation",
            noSort: true
        },
        organization: {
            // in the Providers (flat) view, surface each provider's primary organization as a hyperlink
            // to the Organization profile. Sits after Affiliation Strength. Only rendered by this flat table — the
            // grouped-by-Organization view instead shows the org under the Name column.
            displayText: "Organization",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-organization",
            noSort: true,
            hidden: !featureFlagContext.enabledFeatureFlags.has(Features.VIEW_ORGANIZATIONS_IN_PROVIDER_AFFILIATION_PROVIDERHUB)
        },
        gapsClosed: {
            displayText: "Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-gap-impact",
            tooltip: "Indicates the distinct number of gaps that the provider closes",
            noSort: true,
            hidden: useRegionStatusBreakdown
        },
        activeGapImpact: {
            displayText: "Active Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-active-gap-impact",
            tooltip: "Count of gaps closeable in Active service area counties",
            noSort: true,
            hidden: !useRegionStatusBreakdown
        },
        pendingGapImpact: {
            displayText: "Pending Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-pending-gap-impact",
            tooltip: "Count of gaps closeable in Pending service area counties",
            noSort: true,
            hidden: !useRegionStatusBreakdown
        },
        approvedGapImpact: {
            displayText: "Approved Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-approved-gap-impact",
            tooltip: "Count of gaps closeable in Approved service area counties",
            noSort: true,
            hidden: !useRegionStatusBreakdown
        },
        opportunityGapImpact: {
            displayText: "Opportunity Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-opportunity-gap-impact",
            tooltip: "Count of gaps closeable in Opportunity-to-expand service area counties",
            noSort: true,
            hidden: !useRegionStatusBreakdown
        },
        specialtiesImpacted: {
            displayText: "Specialty Groups Impacted",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-specialty",
            noSort: true
        },
        countiesImpacted: {
            displayText: "Counties Impacted",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-county",
            noSort: true
        },
        accessPoints: {
            displayText: "Access Points",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-filtered-oonsp",
            noSort: true
        },
        filteredQuality: {
            displayText: "Effectiveness (Quality)",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-filtered-quality",
            hidden: projectContext.project.showPciData === false,
            noSort: true
        },
        filteredCost: {
            displayText: "Efficiency (Cost)",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-filtered-cost",
            hidden: projectContext.project.showPciData === false,
            noSort: true
        },
        totalClaims: {
            displayText: "Total Claims",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-filteredclaims",
            hidden: projectContext.project.showPciData === false,
            noSort: true
        },
        medicareClaims: {
            displayText: "Medicare Claims",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-medicareclaims",
            hidden: projectContext.project.showPciData === false,
            noSort: true
        },
        medicaidClaims: {
            displayText: "Medicaid Claims",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-medicaidclaims",
            hidden: projectContext.project.showPciData === false,
            noSort: true
        },
        commercialClaims: {
            displayText: "Commercial Claims",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-commercialclaims",
            hidden: projectContext.project.showPciData === false,
            noSort: true
        },
        vaClaims: {
            displayText: "VA Claims",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-vaclaims",
            hidden: projectContext.project.showVaClaimsVolumeColumn === false,
            noSort: true
        },
        tricareClaims: {
            displayText: "TRICARE Claims",
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-tricareclaims",
            hidden: projectContext.project.showTricareClaimsVolumeColumn === false,
            noSort: true
        },
        motiveAppropriateness: {
            displayElement: <ProviderScoreColumnHeader variant="motive" />,
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-motive",
            noSort: true,
            hidden: projectContext.project.showMotiveAppropriatenessScore === false
        },
        careJourneyCostScore: {
            displayElement: <ProviderScoreColumnHeader variant="careJourneyCost" />,
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-carejourneycost",
            noSort: true,
            hidden: projectContext.project.showCareJourneyScores === false
        },
        careJourneyQualityScore: {
            displayElement: <ProviderScoreColumnHeader variant="careJourneyQuality" />,
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-carejourneyquality",
            hidden: projectContext.project.showCareJourneyScores === false,
            noSort: true
        },
        specialtyPrediction: {
            displayElement: (
                <div style={{ alignItems: "center", display: "flex" }}>
                    {"Specialty Prediction"}
                </div>
            ),
            sizeContentToFit: true,
            testId: "provider-hub_contract-entity-table-specialty-prediction",
            tooltip: null,
            noSort: true,
            hidden: !featureFlagContext.enabledFeatureFlags.has(Features.NETWORK_PERFORMANCE_SPECIALTY_PREDICTION_MODEL)
        }
    }

    const providerPaginationProps: WafflesPaginationProps = {
        changePage: (pageNumber: number) => handlePageChange(pageNumber),
        enableBackendPagination: true,
        handlePageSizeChange: (pageSize: number) => handlePageSizeChange(pageSize),
        pageSizeOptions: [50, 100, 150],
        recordsPerPage: props.pageSize,
        resetToFirstPage: props.resetToFirstPage,
        showPageSizeSelector: true,
        totalRecords: props.contractEntityProviderTableData.totalCount
    }
    /* oxlint-enable sort-keys */

    const handlePageSizeChange = (pageSize: number) => {
        props.setPageSize(pageSize);
        props.setPageIndex(1);
    }

    const handlePageChange = async (newPage: number) => {
        if (!props.resetToFirstPage) {
            props.setPageIndex(newPage);
        }
        if (props.resetToFirstPage) {
            props.setResetToFirstPage(false);
        }
    };

    const handleProviderClick = async (provider: ProviderHubContractEntityProviders) => {
        const selectedProviderDetails = await providerHubService.getServicingProviderDetails(projectContext?.project?.projectId, providerHubContractEntityContext.selectedContractEntity.projectRunConfigurationId, provider.servicingProviderKey);
        providerHubContractEntityContext.navigateToProviderProfile(selectedProviderDetails, ProviderProfileReferrerEnum.ContractEntityProfile);
    }

    // navigate to the Organization profile for a provider's primary (professional) organization, in the same tab.
    const handleProviderOrganizationClick = async (provider: ProviderHubContractEntityProviders) => {
        if (!provider.professionalOrganizationNpi) {
            return;
        }
        // The organization profile (and its semantic URL) is scoped to the organization's OWN contract entity,
        // not the one we drilled from — mirroring how the Organization search grid and Hospital services table
        // drill in. Resolve that CE too so the profile is self-consistent and its URL round-trips on refresh
        // (both org and CE re-hydrate under the same id). When it matches the drilled-from CE we already have,
        // reuse it and skip the extra call so the common path is unchanged.
        const organizationContractEntityId = provider.professionalOrganizationContractEntityId ?? selectedContractEntity.contractEntityId;
        const [selectedOrganizationDetails, organizationContractEntity] = await Promise.all([
            providerHubService.getOrganizationDetails(
                projectContext?.project?.projectId,
                selectedContractEntity.projectRunConfigurationId,
                provider.professionalOrganizationNpi,
                organizationContractEntityId),
            organizationContractEntityId === selectedContractEntity.contractEntityId
                ? Promise.resolve(selectedContractEntity)
                : providerHubService.getContractEntityDetails(
                    projectContext?.project?.projectId,
                    selectedContractEntity.projectRunConfigurationId,
                    organizationContractEntityId),
        ]);
        providerHubContractEntityContext.navigateToOrganizationProfile(selectedOrganizationDetails, organizationContractEntity, ProviderProfileReferrerEnum.ContractEntityProfile);
    }

    const contractEntitiesProvidersSort = (records: ProviderHubContractEntityProvidersTableData[], column: keyof ProviderHubContractEntityProvidersTableData, direction: SortDirections) => {
        const data = [...records];
        if (direction === SortDirections.None) {
            return data;
        }
        const multiplier = direction === SortDirections.Asc ? 1 : -1;
        switch (column) {
            case "providerActionStatuses":
                return data.sort((a, b) => (a.filteredStatuses[0] > b.filteredStatuses[0] ? 1 : -1) * multiplier);
            case "nameElement":
                return data.sort((a, b) => (a.name > b.name ? 1 : -1) * multiplier);
            case "specialtiesImpacted":
            case "countiesImpacted":
                return data.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
            case "affiliationStrength":
            case "gapsClosed":
            case "accessPoints":
            case "effectivenessRank":
            case "efficiencyRank":
            case "totalClaimVolume":
            case "medicareClaimVolume":
            case "careJourneyCost":
            case "careJourneyQuality":
                return data.sort(
                    (a, b) => {
                        return ((a[column] || 0) > (b[column] || 0) ? 1 : -1) * multiplier;
                    });
        }
    }

    /* oxlint-disable sort-keys */
    const contractEntityTableData: ProviderHubContractEntityProvidersTableData[] = useMemo<ProviderHubContractEntityProvidersTableData[]>(() => {

        let data: ProviderHubContractEntityProvidersTableData[] = [];
        data = props.contractEntityProviderTableData.items.map((provider) => {
            return {
                key: provider.servicingProviderKey,
                nameElement: <div className="provider-hub-providers-view__provider-name-content">
                    <QESButton
                        qesType="link"
                        qesTestId={`provider-hub-contractentity-view__provider--${provider.name}`}
                        onClick={() => { handleProviderClick(provider) }}
                        className="demibold provider-hub-providers-view__provider-name-button"
                    >
                        <span className="provider-hub-providers-view__provider-name">{provider.name ?? "---"}</span>
                    </QESButton>
                    <NetworkIntegrityNotificationFlag hasNetworkIntegrityFailure={provider.hasNetworkIntegrityFailure} />
                </div>,
                name: provider.name ?? "---",
                identifier: provider.npi,
                providerNetworkTypes: (
                    <>
                        {sortProviderNetworkTypes(provider.providerNetworkTypes).map((networkType) =>
                            <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} key={networkType} providerNetworkType={networkType} />
                        )}
                    </>
                ),
                providerActionStatuses: provider.providerActionStatuses.length > 0 ? (
                    <>
                        {provider.providerActionStatuses.map((status, index) =>
                            <ProviderActionStatusBadge badgeStyle={ProviderBadgeStyle.Dot} key={index} status={status} />
                        )}
                    </>
                ) : (
                    <>{NO_ACTIONS_APPLIED_LABEL}</>
                ),
                filteredStatuses: provider.providerActionStatuses.map(status => status),
                affiliationStrength: Math.round((provider.affiliationStrength ?? 0) * 100),
                organization: provider.professionalOrganizationNpi
                    ? <QESButton
                        qesType="link"
                        qesTestId={`provider-hub-contractentity-view__organization--${provider.professionalOrganizationNpi}`}
                        onClick={() => { handleProviderOrganizationClick(provider) }}
                        className="demibold"
                    >
                        {provider.professionalOrganizationName ?? provider.professionalOrganizationNpi}
                    </QESButton>
                    : "---",
                gapsClosed: formatGapImpactCount(provider.gapsClosed),
                activeGapImpact: formatGapImpactCount(provider.activeGapsClosed),
                pendingGapImpact: formatGapImpactCount(provider.pendingGapsClosed),
                approvedGapImpact: formatGapImpactCount(provider.approvedGapsClosed),
                opportunityGapImpact: formatGapImpactCount(provider.opportunityGapsClosed),
                specialtiesImpacted: <WaffleRowTooltip
                    containerClassName="provider-hub-waffle-tooltip"
                    elements={provider.specialtiesImpacted?.map(formatSpecialtyWithCode)}
                    numberOfElementsPerRow={3} />,
                countiesImpacted: <WaffleRowTooltip
                    containerClassName="provider-hub-waffle-tooltip"
                    elements={formatCountiesImpacted(provider.countiesImpacted)}
                    numberOfElementsPerRow={3} />,
                accessPoints: provider.accessPointCount,
                filteredQuality: formatScore(provider.effectivenessRank),
                filteredCost: formatScore(provider.efficiencyRank),
                totalClaims: provider.totalClaimVolume ?? '---',
                medicareClaims: provider.medicareClaimVolume ?? '---',
                medicaidClaims: provider.medicaidClaimVolume ?? '---',
                commercialClaims: provider.commercialClaimVolume ?? '---',
                vaClaims: provider.vaClaimVolume ?? '---',
                tricareClaims: provider.tricareClaimVolume ?? '---',
                motiveAppropriateness: formatScore(provider.motiveAppropriateness),
                careJourneyCostScore: formatScore(provider.careJourneyCost),
                careJourneyQualityScore: formatScore(provider.careJourneyQuality),
                specialtyPrediction: <WaffleRowTooltip
                    containerClassName="provider-hub-waffle-tooltip"
                    elements={sortPredictedSpecialtyNames(provider.predictedSpecialtyNames)}
                    numberOfElementsPerRow={3}
                    separator=", " />,
            }
        });

        return data;

    }, [props.contractEntityProviderTableData]);
    /* oxlint-enable sort-keys */
    return (
        <PageSection className="contract-entity-details">
            <Waffles<ProviderHubContractEntityProvidersTableData>
                columns={providerHubContractEntitiesTableColumnConfig}
                data={contractEntityTableData}
                totalNumberOfRecords={props.contractEntityProviderTableData.totalCount}
                enablePagination={true}
                loading={props.loading}
                emptyTableMessage="No matching practitioners found. Please start a new search"
                fullWidth={true}
                paginationProps={providerPaginationProps}
                testId="provider-hub__contractentity-table-contract-entity-details"
                customSort={contractEntitiesProvidersSort}
                theme={WafflesThemes.ContractEntityDetails}
                disableStickyHeader={true}
                variant="frozen-border"
            />
        </PageSection>
    )
}

export default ContractEntityProfileTable;